import { useEffect, useState } from "react";
import { Star, X, Quote } from "lucide-react";
import { useContent } from "../hooks/useContent";

const ALLOWED_AREAS = [
  "nirman nagar",
  "rangoli garden",
  "vaishali nagar",
  "chordiya city",
  "amrapali",
];

export const FloatingReviews = () => {
  const { getSection } = useContent();
  const all = getSection("testimonials") || [];
  const reviews = all.filter((t) =>
    ALLOWED_AREAS.some((a) => (t.location || "").toLowerCase().includes(a))
  );

  const [open, setOpen] = useState(true);
  const [idx, setIdx] = useState(0);
  const [dismissedSession, setDismissedSession] = useState(false);

  useEffect(() => {
    if (!reviews.length || dismissedSession) return;
    const t = setInterval(() => {
      setIdx((p) => (p + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(t);
  }, [reviews.length, dismissedSession]);

  if (!reviews.length || !open || dismissedSession) return null;
  const review = reviews[idx];

  return (
    <div
      className="fixed left-4 bottom-24 sm:bottom-6 z-40 max-w-[300px] sm:max-w-xs animate-[slideInLeft_0.5s_ease-out]"
      data-testid="floating-reviews-widget"
    >
      <div className="relative bg-white rounded-2xl border border-sky-100 shadow-xl shadow-sky-900/10 p-4 pr-9">
        <button
          onClick={() => {
            setOpen(false);
            setDismissedSession(true);
          }}
          className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close reviews"
          data-testid="floating-reviews-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
            {review.avatar}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-900 truncate">{review.name}</p>
            <p className="text-[10px] text-slate-400 truncate">{review.location}</p>
          </div>
        </div>

        <div className="flex items-center gap-0.5 mb-1.5">
          {Array.from({ length: review.rating }).map((_, j) => (
            <Star key={j} className="w-3 h-3 text-lime-500 fill-lime-500" />
          ))}
          <span className="ml-1 text-[10px] text-slate-400 font-medium">Google Review</span>
        </div>

        <div className="relative">
          <Quote className="w-4 h-4 text-sky-100 absolute -top-0.5 -left-0.5" />
          <p className="text-xs text-slate-600 leading-snug pl-4 line-clamp-4">
            {review.text}
          </p>
        </div>

        {/* Dots */}
        <div className="flex items-center gap-1 mt-3">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1 rounded-full transition-all ${
                i === idx ? "bg-sky-600 w-4" : "bg-slate-200 w-1.5"
              }`}
              aria-label={`Show review ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
